import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import {
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SkLoading from "components/SkLoading";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";

const columns = [
  { Header: "S.No", accessor: "no" },
  { Header: "Heading", accessor: "heading" },
  { Header: "Description", accessor: "description" },
  { Header: "Status", accessor: "status" },
  { Header: "Action", accessor: "action" },
];

const ChatComponent = ({ refreshKey, onEdit }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowsData, setRowsData] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchHelpSupport = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("https://api.homelyx.framekarts.com/api/help-support", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        setData(res.data.data);
      }
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://api.homelyx.framekarts.com/api/help-support/delete/${itemToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchHelpSupport();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  useEffect(() => {
    setLoading(true);
    fetchHelpSupport();
  }, [refreshKey]);

  useEffect(() => {
    if (data && data.length > 0) {
      const rows = data.map((item, index) => ({
        no: (
          <MDTypography sx={{ fontSize: 12, fontWeight: "medium" }} variant="text">
            {index + 1}
          </MDTypography>
        ),
        heading: (
          <MDTypography sx={{ fontSize: 12.5, fontWeight: "medium" }} variant="text">
            {item.heading || "N/A"}
          </MDTypography>
        ),
        description: (
          <MDTypography sx={{ fontSize: 12.5 }} variant="text">
            {item.description || "N/A"}
          </MDTypography>
        ),
        status: (
          <Chip
            label={item.diasble ? "Disabled" : "Active"}
            color={item.diasble ? "error" : "success"}
            size="small"
            sx={{ fontWeight: 600, minWidth: 80 }}
          />
        ),
        action: (
          <MDBox display="flex" alignItems="center">
            <IconButton
              aria-label="edit_help_support"
              onClick={() => {
                if (onEdit) onEdit(item);
              }}
            >
              <Edit
                sx={({ palette: { dark, white, info } }) => ({
                  color: darkMode ? info.main : dark.main,
                })}
              />
            </IconButton>
            <IconButton
              aria-label="delete_help_support"
              onClick={() => handleDeleteClick(item._id || item.id)}
            >
              <Delete color="error" />
            </IconButton>
          </MDBox>
        ),
      }));
      setRowsData(rows);
    } else {
      setRowsData([]);
    }
  }, [data, onEdit, darkMode]);

  return (
    <MDBox py={1}>
      {loading ? (
        <SkLoading />
      ) : data && data.length > 0 ? (
        <DataTable
          table={{
            columns: columns,
            rows: rowsData,
          }}
          isSorted={false}
          entriesPerPage={false}
          isPages={data && data.length}
          noEndBorder
          canSearch={false}
          showTotalEntries={false}
          pagination={false}
          isPagination={false}
        />
      ) : (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <MDTypography variant="h5">Help &amp; Support Not Found ...!</MDTypography>
        </MDBox>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "10px",
            backgroundColor: darkMode ? "#202940" : "#ffffff",
          },
        }}
      >
        <DialogTitle id="alert-dialog-title">
          <MDTypography variant="h5" fontWeight="medium" color={darkMode ? "white" : "dark"}>
            Delete Help & Support?
          </MDTypography>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete this item? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            sx={{ color: darkMode ? "#ffffff" : "#344767" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            autoFocus
            disabled={isDeleting}
            sx={{
              backgroundColor: "#1A73E8",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#1557b0" },
            }}
          >
            {isDeleting ? <CircularProgress size={24} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

export default ChatComponent;

ChatComponent.propTypes = {
  refreshKey: PropTypes.number,
  onEdit: PropTypes.func,
};
